import {normalizeReturnToPath} from './returnTo'

const STORAGE_KEY = 'client_navigation_stack'
const MAX_ENTRIES = 10
const MAX_ENTRY_LENGTH = 4096

const canUseStorage = () => {
    try { return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined' } catch { return false }
}

// Persist only navigation identity. Search text, auth parameters, bearer-link
// routes and fragments are not needed to decide whether history.back is valid.
const NAVIGATION_ROOTS = new Set(['', 'home', 'calendar', 'my-classes', 'payments', 'profile', 'studio', 'courses',
    'cennik', 'indywidualne', 'kursy', 'o-studiu', 'polityka-prywatnosci', 'regulamin', 'wydarzenia', 'zajecia', 'embed'])
const NAVIGATION_QUERY_KEYS = new Set(['signupId', 'historyState', 'view', 'date', 'locationId', 'instructorId',
    'classTypeId', 'type', 'state', 'filter', 'tab', 'page', 'pageSize', 'currentPage', 'historyPage', 'schedulePage',
    'templateId', 'paymentOrderId', 'offerId', 'trainerId', 'startAt', 'mode', 'mine', 'available', 'required', 'source'])

export const normalizeNavigationPath = (entry: unknown): string | null => {
    if (typeof entry !== 'string' || entry.length === 0 || entry.length > MAX_ENTRY_LENGTH) return null
    const safe = normalizeReturnToPath(entry)
    if (!safe) return null
    const url = new URL(safe, 'http://localhost')
    if (!NAVIGATION_ROOTS.has(url.pathname.split('/')[1] ?? '')) return null
    for (const [key, value] of Array.from(url.searchParams)) {
        if (!NAVIGATION_QUERY_KEYS.has(key) || !/^[a-zA-Z0-9_.:,\-]{1,128}$/.test(value)) url.searchParams.delete(key)
    }
    url.searchParams.sort()
    return `${url.pathname}${url.search}`
}

export const readNavigationStack = (): string[] => {
    if (!canUseStorage()) return []
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed
            .map(normalizeNavigationPath)
            .filter((entry): entry is string => entry !== null)
            .slice(-MAX_ENTRIES)
    } catch {
        return []
    }
}

const writeNavigationStack = (stack: string[]) => {
    if (!canUseStorage()) return
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack))
    } catch {
        // no-op
    }
}

export const clearNavigationStack = () => {
    if (!canUseStorage()) return
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // no-op
    }
}

export const popNavigationStack = () => {
    const stack = readNavigationStack()
    if (stack.length === 0) return stack
    stack.pop()
    writeNavigationStack(stack)
    return stack
}

const ENTRIES_KEY = 'client_navigation_entries_v1'
const MAX_SAVED_ENTRIES = 80

/** Each history entry owns its predecessor chain. Revisited URLs remain distinct visits. */
export const trackNavigationEntry = (entryKey: string, url: string, kind: 'push' | 'replace' | 'pop') => {
    if (!canUseStorage() || !entryKey) return
    let path: string
    try {
        const parsed = new URL(url, window.location.href)
        if (parsed.origin !== window.location.origin) return
        const safePath = normalizeNavigationPath(`${parsed.pathname}${parsed.search}`)
        if (!safePath) {
            clearNavigationStack()
            return
        }
        path = safePath
    } catch { return }
    let entries: Record<string, string[]> = {}
    try {
        const parsed: unknown = JSON.parse(sessionStorage.getItem(ENTRIES_KEY) ?? '{}')
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            for (const [key, value] of Object.entries(parsed)) {
                if (!Array.isArray(value)) continue
                const paths = value.map(normalizeNavigationPath).filter((item): item is string => item !== null).slice(-MAX_ENTRIES)
                if (paths.length) entries[key] = paths
            }
        }
    } catch { /* Start a fresh local chain if storage is unavailable. */ }
    const previous = readNavigationStack()
    const next = kind === 'pop'
        ? entries[entryKey] ?? [path]
        : kind === 'replace'
            ? [...previous.slice(0, -1), path]
            : [...previous, path].slice(-MAX_ENTRIES)
    // A replace may change query state after a Back; the last path must match reality.
    next[next.length - 1] = path
    writeNavigationStack(next)
    delete entries[entryKey]
    entries[entryKey] = next
    entries = Object.fromEntries(Object.entries(entries).slice(-MAX_SAVED_ENTRIES))
    try { sessionStorage.setItem(ENTRIES_KEY, JSON.stringify(entries)) } catch { /* Back degrades to the safe parent. */ }
}
